<script lang="ts">
  // Unified proposal-history timeline per SPEC-CHANGE-CANDIDATES item
  // 14. Fetches the server-side merged event list (PR + reviews +
  // commits) and enriches client-side with manifest audit stamps
  // ([Publish now], [De-queue]) looked up from the catalogue-store
  // cache by git_hash. Renders a vertical timeline with one event per
  // row: icon, actor, locale-formatted timestamp, summary, and an
  // expandable body for events that carry one (review bodies, commit
  // messages, dequeue reasons).
  //
  // Replaces the prior `<ol class="review-thread">` rendering in
  // ReviewInterface.svelte and the `<ol class="pinned-review__history">`
  // rendering in RevisionEditor.svelte. Both surfaces now show the
  // same chronological view of what each actor has done.

  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { catalogueState } from '../lib/catalogue-store.svelte';
  import {
    enrichTimelineWithManifestEntry,
    findManifestEntryByHash,
    displayFor,
    formatTimelineTimestamp
  } from '../lib/proposal-timeline-helpers';
  import type { TimelineEvent } from '../lib/types';

  interface Props {
    prNumber: number;
  }

  const { prNumber }: Props = $props();

  let serverEvents = $state<TimelineEvent[] | null>(null);
  let mergeCommitSha = $state<string | null>(null);
  let loadError = $state<string | null>(null);
  let loading = $state<boolean>(false);
  let expanded = $state<Set<string>>(new Set<string>());

  async function load() {
    loading = true;
    loadError = null;
    try {
      const response = await api.loadProposalTimeline(prNumber);
      serverEvents = response.events;
      mergeCommitSha = response.merge_commit_sha;
    } catch (err) {
      loadError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error
        ? err.message
        : 'timeline_load_failed';
    } finally {
      loading = false;
    }
  }

  onMount(load);

  // Re-enrich whenever the manifest cache updates or the candidate
  // hash changes. The server response is unchanged; the manifest
  // stamps are layered on top so the timeline reflects [Publish now]
  // / [De-queue] actions as soon as the SPA's manifest refreshes.
  const events = $derived.by<TimelineEvent[]>(() => {
    if (!serverEvents) return [];
    const entry = findManifestEntryByHash(catalogueState.manifest, mergeCommitSha);
    return enrichTimelineWithManifestEntry(serverEvents, entry);
  });

  function toggleExpanded(key: string) {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    expanded = next;
  }

  function keyFor(event: TimelineEvent, index: number): string {
    return `${event.source}-${event.source_id ?? index}-${event.timestamp_utc}`;
  }
</script>

<section class="proposal-timeline">
  {#if loading}
    <p class="muted">Loading timeline…</p>
  {:else if loadError}
    <p class="banner banner-fail">Timeline failed to load: {loadError}</p>
    <button class="btn-link" type="button" onclick={load}>Retry</button>
  {:else if events.length === 0}
    <p class="muted">No timeline events yet.</p>
  {:else}
    <ol class="proposal-timeline__list">
      {#each events as event, i (keyFor(event, i))}
        {@const display = displayFor(event.type)}
        {@const key = keyFor(event, i)}
        {@const isOpen = expanded.has(key)}
        <li class="timeline-event {display.cssClass}">
          <span class="timeline-event__icon" aria-hidden="true">{display.icon}</span>
          <div class="timeline-event__body">
            <div class="timeline-event__heading">
              <span class="timeline-event__label">{display.label}</span>
              <span class="timeline-event__actor">
                {event.actor.display_name ?? event.actor.login}
              </span>
              <time class="timeline-event__time" datetime={event.timestamp_utc}>
                {formatTimelineTimestamp(event.timestamp_utc)}
              </time>
            </div>
            <div class="timeline-event__summary">{event.summary}</div>
            {#if event.body}
              {#if event.body.length > 240 && !isOpen}
                <button class="btn-link" type="button" onclick={() => toggleExpanded(key)}>
                  Show full message
                </button>
              {:else}
                <pre class="timeline-event__body-text">{event.body}</pre>
                {#if event.body.length > 240}
                  <button class="btn-link" type="button" onclick={() => toggleExpanded(key)}>
                    Collapse
                  </button>
                {/if}
              {/if}
            {/if}
          </div>
        </li>
      {/each}
    </ol>
  {/if}
</section>

<style>
  .proposal-timeline { display: flex; flex-direction: column; gap: var(--space-2); }
  .proposal-timeline__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
    border-left: 2px solid var(--surface-line-soft);
    padding-left: var(--space-3);
  }
  .timeline-event {
    display: grid;
    grid-template-columns: 2rem 1fr;
    gap: var(--space-2);
    padding: var(--space-2) 0;
    border-bottom: 1px solid var(--surface-line-soft);
    position: relative;
  }
  .timeline-event:last-child { border-bottom: none; }
  .timeline-event__icon {
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--surface-card, #f4f4f4);
    color: var(--surface-fg);
    font-size: var(--type-md);
    margin-left: -2.5rem;
    border: 1px solid var(--surface-line-soft);
  }
  .timeline-event__heading {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    align-items: baseline;
    font-size: var(--type-sm);
  }
  .timeline-event__label {
    font-weight: 600;
  }
  .timeline-event__actor {
    color: var(--surface-muted);
  }
  .timeline-event__time {
    color: var(--surface-muted);
    font-variant-numeric: tabular-nums;
    margin-left: auto;
  }
  .timeline-event__summary { font-size: var(--type-sm); }
  .timeline-event__body-text {
    white-space: pre-wrap;
    word-break: break-word;
    background: var(--surface-card, #f9f9f9);
    padding: var(--space-2);
    border-radius: var(--radius-sm);
    font-size: var(--type-sm);
    margin: var(--space-1) 0 0 0;
  }
  /* Per-event-type accents — colour the icon background so the
     timeline reads at a glance: approval green, request-changes
     amber, merged blue, dismissed grey. Falls back to neutral when
     the project's design tokens do not define a status colour. */
  .timeline-event--opened .timeline-event__icon { background: var(--status-info-soft, #e3eefc); }
  .timeline-event--comment .timeline-event__icon { background: var(--surface-card, #f4f4f4); }
  .timeline-event--request-changes .timeline-event__icon { background: var(--status-warn-soft, #fff4d4); }
  .timeline-event--revision .timeline-event__icon { background: var(--status-info-soft, #e3eefc); }
  .timeline-event--approve .timeline-event__icon { background: var(--status-pass-soft, #d9f7d4); }
  .timeline-event--merged .timeline-event__icon { background: var(--status-pass-soft, #d9f7d4); }
  .timeline-event--closed .timeline-event__icon { background: var(--surface-card, #ebebeb); }
  .timeline-event--dismissed .timeline-event__icon { background: var(--surface-card, #ebebeb); }
  .timeline-event--published .timeline-event__icon { background: var(--status-warn-soft, #fff4d4); }
  .timeline-event--dequeued .timeline-event__icon { background: var(--status-fail-soft, #fdd); }
</style>
